import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogObjetivoV2DashboardDetalhe from "pages/components/DialogObjetivoV2DashboardDetalhe";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import CircularProgress from "@mui/material/CircularProgress";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

function toISODate(value) {
  if (!value) return "";

  return String(value).slice(0, 10);
}

function fmtNumero(value, casas = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function fmtPercentagem(value, casas = 1) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return `${n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;
}

function casasPorMetrica(metrica, tipo = "resultado", modo = "") {
  const m = String(metrica || "")
    .trim()
    .toUpperCase();

  const modoNorm = String(modo || "")
    .trim()
    .toUpperCase();

  if (m === "CLIENTES") {
    return modoNorm === "MEDIA" ? 3 : 0;
  }

  if (m === "QTD") {
    return 0;
  }

  if (m === "EUROS") {
    return 2;
  }

  if (m === "LITROS") {
    return tipo === "objetivo" ? 0 : 3;
  }

  return 2;
}

export default function ObjetivosV2EstadoDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
  onEmptyChange,
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [carregado, setCarregado] = useState(false);

  const [openDetalhe, setOpenDetalhe] = useState(false);
  const [objetivoDetalhe, setObjetivoDetalhe] = useState(null);

  const carregar = async () => {
    setLoading(true);
    onLoadingChange?.(true);

    try {
      const [data, err] = await handleApi(
        api.post("Comercial/SelObjetivosV2Resumo", {
          apenasVigentes: true,
        })
      );

      if (err) {
        setRows([]);
        setCarregado(true);
        onLoadedChange?.(true);
        onEmptyChange?.(true);
        return;
      }

      const arr = Array.isArray(data) ? data : data?.data || [];

      setRows(arr);
      setCarregado(true);

      onLoadedChange?.(true);
      onEmptyChange?.(arr.length === 0);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) {
      return;
    }

    carregar();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken]);

  const columns = useMemo(
    () => [
      {
        Header: "Objetivo",
        accessor: "objetivo_nome",
        width: 260,
      },
      {
        Header: "Início",
        accessor: "dataini",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Métrica",
        accessor: "metrica",
        width: 100,
      },
      {
        Header: "Objetivo",
        accessor: "valor_objetivo",
        width: 110,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) =>
          fmtNumero(
            value,
            casasPorMetrica(row?.original?.metrica, "objetivo", row?.original?.patamares_modo)
          ),
      },
      {
        Header: "Resultado",
        accessor: "resultado",
        width: 110,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) =>
          fmtNumero(
            value,
            casasPorMetrica(row?.original?.metrica, "resultado", row?.original?.patamares_modo)
          ),
      },
      {
        Header: "Atingimento",
        accessor: "atingimento_percentagem",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtPercentagem(value, 1),
      },
      {
        Header: "Contrapartida",
        accessor: "contrapartida_percentagem",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) =>
          value === null || value === undefined ? "" : fmtPercentagem(value, 2),
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        id: "detalhe",
        tooltip: "Ver detalhe",
        icon: <InfoOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: (row) => {
          setObjetivoDetalhe(row);
          setOpenDetalhe(true);
        },
      },
    ],
    []
  );

  const grupos = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const idGrupo = row?.idobjetivogrupo ?? 0;

      const nomeGrupo = String(row?.grupo_nome || "Sem grupo").trim() || "Sem grupo";

      if (!map.has(idGrupo)) {
        map.set(idGrupo, {
          id: idGrupo,
          nome: nomeGrupo,
          rows: [],
          idsObjetivos: new Set(),
        });
      }

      const grupo = map.get(idGrupo);

      grupo.rows.push(row);

      if (row?.id != null) {
        grupo.idsObjetivos.add(String(row.id));
      }
    });

    return Array.from(map.values())
      .map((grupo) => ({
        id: grupo.id,
        nome: grupo.nome,
        rows: grupo.rows,
        totalObjetivos: grupo.idsObjetivos.size,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-PT"));
  }, [rows]);

  return (
    <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
      {loading && carregado ? (
        <MDBox py={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </MDBox>
      ) : null}

      {carregado && grupos.length > 0 && (
        <MDBox
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {grupos.map((grupo) => {
            const textoQuantidade =
              grupo.totalObjetivos === 1 ? "1 objetivo" : `${grupo.totalObjetivos} objetivos`;

            return (
              <DashboardLazyAccordion
                key={`objetivos-v2-grupo-${grupo.id}`}
                title={`${grupo.nome} (${textoQuantidade})`}
                loaded
                showRefreshButton={false}
              >
                <ProjectTable
                  tableKey={`dashboard-objetivos-v2-${grupo.id}`}
                  version="v1"
                  columns={columns}
                  data={grupo.rows}
                  rowActions={rowActions}
                  actionsPosition="left"
                  emptyMessage="Sem objetivos para apresentar."
                  minWidth={1000}
                  maxHeight={360}
                  showKeepFilters
                />
              </DashboardLazyAccordion>
            );
          })}
        </MDBox>
      )}

      {carregado && grupos.length === 0 && !loading && (
        <MDBox py={2}>
          <MDTypography
            variant="button"
            color="text"
            sx={{
              display: "block",
              textAlign: "center",
              fontWeight: "regular",
            }}
          >
            Não foram encontrados objetivos V2 para apresentar.
          </MDTypography>
        </MDBox>
      )}

      <DialogObjetivoV2DashboardDetalhe
        open={openDetalhe}
        onClose={() => {
          setOpenDetalhe(false);
          setObjetivoDetalhe(null);
        }}
        objetivo={objetivoDetalhe}
      />
    </MDBox>
  );
}
